import type { MobileSection } from "./mobileNavigation.ts";

// N4-4: which bottom-tab sections show a "a decision is waiting for you" red dot.
// Discipline: a dot means "has / has not" — never a number — and only ever
// lights for an actual pending decision. Pure informational activity (unread
// news, other teams' moves) never lights a tab; those signals are deliberately
// absent from the inputs below.
export type MobileTabAlertSignals = {
	// market: an actual blocker you must clear — a real pending negotiation or
	//   expansion protection, never the phase name alone.
	hasPendingNegotiations?: boolean;
	needsExpansionProtection?: boolean;
	// home: the owner is waiting on a decision in the inbox (a "must-decide"
	//   message, i.e. classifyInboxMessage === "task").
	homeInboxDecision?: boolean;
	// team: the roster is illegal — too few players (short) or over the roster
	//   limit. An exactly-full legal roster is informational, not a decision, so the
	//   worker leaves this false for it.
	teamRosterAlert?: boolean;
};

// At most this many tabs may show a dot at once (§六: "数量封顶"). Only three of
// the five tabs can ever light, so today the cap never actually trims anything;
// it is here so the rule stays correct if more tabs gain badges later.
export const MAX_RED_DOT_TABS = 3;

// Priority when the cap has to trim: a play-menu blocker (market) hard-stops the
// phase, so it wins; then the owner's inbox decision (home); then roster legality
// (team), which is important but not phase-blocking.
const TAB_PRIORITY: MobileSection[] = ["market", "home", "team"];

export const getMobileTabRedDots = (
	signals: MobileTabAlertSignals,
): Set<MobileSection> => {
	const lit = new Set<MobileSection>();

	if (signals.hasPendingNegotiations || signals.needsExpansionProtection) {
		lit.add("market");
	}
	if (signals.homeInboxDecision) {
		lit.add("home");
	}
	if (signals.teamRosterAlert) {
		lit.add("team");
	}

	return new Set(
		TAB_PRIORITY.filter((section) => lit.has(section)).slice(
			0,
			MAX_RED_DOT_TABS,
		),
	);
};
